import { type ToastMessage, uiStore } from '../../stores/index.ts';

/**
 * 화면 우상단에 토스트 stack을 표시하는 글로벌 컴포넌트.
 * uiStore.toasts를 구독해 자동 재렌더. 페이지 전환과 무관하게 살아있음.
 *
 * 토스트의 만료/제거는 stores/index.ts의 setTimeout이 책임.
 * 본 컴포넌트는 표시만.
 */
export class ToastContainer {
  private root: HTMLElement;
  private unsubscribe: (() => void) | null = null;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className =
      'toast-container fixed z-[60] flex flex-col items-end gap-2 pointer-events-none';
    this.root.style.top = 'max(1rem, env(safe-area-inset-top))';
    this.root.style.right = 'max(1rem, env(safe-area-inset-right))';
    this.root.setAttribute('aria-live', 'polite');
    this.root.setAttribute('aria-atomic', 'false');
    parent.appendChild(this.root);
  }

  mount(): void {
    this.render(uiStore.get().toasts);
    this.unsubscribe = uiStore.subscribe((next, prev) => {
      if (next.toasts === prev.toasts) return;
      this.render(next.toasts);
    });
  }

  unmount(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.root.remove();
  }

  private render(toasts: readonly ToastMessage[]): void {
    this.root.innerHTML = '';
    for (const toast of toasts) {
      this.root.appendChild(renderToast(toast));
    }
  }
}

function renderToast(toast: ToastMessage): HTMLElement {
  const el = document.createElement('div');
  el.dataset.toastId = toast.id;
  el.dataset.level = toast.level;
  el.className = [
    'toast pointer-events-auto rounded-xl px-4 py-2.5 text-sm font-medium shadow-lg ring-1 max-w-xs',
    levelClass(toast.level),
  ].join(' ');
  el.textContent = `${prefix(toast.level)} ${toast.text}`;
  return el;
}

function levelClass(level: ToastMessage['level']): string {
  switch (level) {
    case 'error':
      return 'bg-rose-600 text-white ring-rose-400/40';
    case 'warn':
      return 'bg-card-gold text-wood-ink ring-card-brass/40';
    default:
      return 'bg-wood-bark text-wood-paper ring-wood-coffee/40';
  }
}

function prefix(level: ToastMessage['level']): string {
  switch (level) {
    case 'error':
      return '⚠';
    case 'warn':
      return '⚠';
    default:
      return 'ℹ';
  }
}
